//
// Copyright 2021 Emerald Associates, Inc.  All Rights Reserved.
//
// Use of this file other than by Emerald Associates, Inc. is forbidden
// unless otherwise authorized by a separate written license agreement.
//
// $Id$
//
import React, {useState} from "react";
import {makeStyles} from "@material-ui/core/styles";
import Typography from "@material-ui/core/Typography";
import "react-bootstrap-table-next/dist/react-bootstrap-table2.min.css";
import {useDropzone} from "react-dropzone";
import Button from "@material-ui/core/Button";
import FilesIcon from "@material-ui/icons/FileCopy";
import UploadButton from "src/components/loader/buttons/UploadButton";
import ConfigurationCheckBox from "eai-configurator-ui/components/configuration/base/ConfigurationCheckBox";
import ConfigurationComboBox from "eai-configurator-ui/components/configuration/base/ConfigurationComboBox";
import Paper from "@material-ui/core/Paper";
import Grid from "@material-ui/core/Grid";
import Item from "@material-ui/core/Grid";
import ConfigurationTextField from "eai-configurator-ui/components/configuration/base/ConfigurationTextField";

const useStyles = makeStyles((theme) => ({
  root: {
    "& > *": {
      margin: theme.spacing(1),
    },
    width: "100%",
  },
  textField: {
    width: "100%",
  },
  error: {
    color: "red",
  },
  checkbox: {
    paddingLeft: 0,
  },
  paper: {
    padding: 10,
  },
  button: {
    margin: 4,
  },
  expandablePanelIcon: {
    margin: 4,
  },
  expandablePanel: {
    paddingLeft: 10,
  },
  input: {
    display: "none",
  },
  dropzone: {
    flex: 1,
    display: "flex",
    flexDirection: "column",
    alignItems: "center",
    padding: "20px",
    borderWidth: 2,
    borderRadius: 2,
    borderColor: "#eeeeee",
    borderStyle: "dashed",
    backgroundColor: "#fafafa",
    color: "#bdbdbd",
    outline: "none",
    transition: "border .24s ease-in-out",
  },
}));
const UploadPage = ({disabled, navigateToResults, dropzoneOptions}) => {
  const [selectedFiles, setSelectedFiles] = useState([]);

  const {
    getRootProps,
    getInputProps,
    open: handleOpenFileSelection,
  } = useDropzone({
    // accept: 'image/jpeg, image/png'
    onDrop: (files) => setSelectedFiles(files),
    ...dropzoneOptions,
  });

  const clearSelectedFiles = () => {
    setSelectedFiles([]);
  };

  const selectedFilesItems = selectedFiles.map((file) => (
    <li key={file.path}>
      {file.path} - {file.size} bytes
    </li>
  ));
  const classes = useStyles();
  return (
    <React.Fragment>
      <div>
        <Typography variant="body2" color="textSecondary">
          <h5>Upload from Excel</h5>
          <hr/>
        </Typography>

        <Paper className={classes.paper}>
          <Typography variant="body2" color="textSecondary">
            <h6>Upload file</h6>
          </Typography>
          <React.Fragment>
            <div {...getRootProps({className: classes.dropzone})}>
              <input {...getInputProps()} />
              <p>Drag 'n' drop some files here, or click to select files</p>
            </div>
            <aside>
              <ul>{selectedFilesItems}</ul>
            </aside>
          </React.Fragment>
          <Button
            variant="contained"
            color="primary"
            className={classes.button}
            startIcon={<FilesIcon/>}
            onClick={handleOpenFileSelection}
          >
            Choose File(s)
          </Button>
          <UploadButton
            className={classes.button}
            selectedFiles={selectedFiles}
            clearSelectedFiles={clearSelectedFiles}
            disabled={selectedFilesItems.length === 0 || disabled}
            handleSuccess={navigateToResults}
          />
        </Paper>

        <hr/>

        <Paper className={classes.paper}>
          <Typography variant="body2" color="textSecondary">
            <h6>Import options</h6>
          </Typography>
          <Typography variant="caption" color="textSecondary">
            Use this area to customize the import options.
            <br/>
            To <b>delete</b> data in P6, there should be a column in the Excel file named "Delete Record" with the value "DELETE" in the records you wish to
            delete, and the [Enable Deletes] checkbox should be checked.
            <br/>
            If the [Enable Inserts] checkbox is unchecked, no new records are added to the database. If the [Enable Updates] checkbox, no updates are made to
            the database.
            <br/>
            If the [Load Codes] checkbox is unchecked, code columns, such as "Code():XXX" will be ignored. If the [Load UDFs] checkbox is unchecked, UDF
            columns, such as "UDF():XXX" are ignored.
            <br/>
            If the [Load Process One Row At a Time] checkbox is checked, then the P6-Loader will process one row of the Excel file at a time. This will severely
            impact the performance, but it can be helpful in case you are having errors loading the Excel file.
          </Typography>
          <Grid container style={{marginTop: 10}}>
            <Item xs={2}>
              <ConfigurationCheckBox path={"loader:upload:enableDeletes"}/>
            </Item>
            <Item xs={2}>
              <ConfigurationCheckBox path={"loader:upload:enableInserts"}/>
            </Item>
            <Item xs={2}>
              <ConfigurationCheckBox path={"loader:upload:enableUpdates"}/>
            </Item>
            <Item xs={2}>
              <ConfigurationCheckBox path={"loader:upload:loadCodes"}/>
            </Item>
            <Item xs={2}>
              <ConfigurationCheckBox path={"loader:upload:loadUdfs"}/>
            </Item>
            <Item xs={4}>
              <ConfigurationCheckBox path={"loader:upload:processOneRowAtATime"}/>
            </Item>
          </Grid>
        </Paper>

        <hr/>

        <Paper className={classes.paper}>
          <Typography variant="body2" color="textSecondary">
            <h6>Unique lookup overwrite</h6>
          </Typography>
          <Typography variant="caption" color="textSecondary">
            Use this area to disable the unique lookup for hierarchical objects, if necessary.
            <br/>
            If a hierarchical object is being loaded, but a path is not being provided
            when the value is being loaded (for example, just a WBS code but not the whole path), the
            P6-Loader can find the object with just that code. In some cases, for example when creating hierarchies where there might be repetitions,
            this may not be wanted, and if the [Prevent Unique Lookups] option is checked, then no lookups using unique values will be performed.
          </Typography>
          <Grid container style={{marginTop: 10}}>
            <Item xs={6}>
              <ConfigurationCheckBox path={"loader:upload:preventUniqueLookups"}/>
            </Item>
          </Grid>
        </Paper>

        <hr/>

        <Paper className={classes.paper}>
          <Typography variant="body2" color="textSecondary">
            <h6>Hierarchy options</h6>
          </Typography>
          <Typography variant="caption" color="textSecondary">
            Use this area to customize the import options related to hierarchical objects.
            <br/>
            The option [Full or Classic] lets you choose the preferred column combination,
            if the Excel file has both the "FullPath" column, as well as the "Parent" and "Code" columns. The "FullPath"
            column is a combination of the parent and code columns, and if you are using it, then the full path can be specified in just one place.
            <br/>
            The option [Primary Separator] lets you specify the primary separator used in the spreadsheet being loaded.
            This must match the separator used in the "FullPath" column. The option [Additional Separators] lets you configure possible
            additional separators used in the file being loaded - for example, if a different separator was used to generate the file than the one this instance
            is configured to use.
          </Typography>
          <Grid container style={{marginTop: 10}}>
            <Item xs={3}>
              <ConfigurationComboBox path={"loader:upload:fullOrClassicLoadDynamicOptionPreferred"}/>
            </Item>
            <Item xs={3}>
              <ConfigurationTextField path={"loader:upload:importSeparator"}/>
            </Item>
            <Item xs={3}>
              <ConfigurationTextField path={"loader:upload:importSeparators"}/>
            </Item>
          </Grid>
        </Paper>
      </div>
    </React.Fragment>
  )
    ;
};
export default UploadPage;
